<template>
  <div
    class="fpui-input-text"
    :class="themeClass"
    @keyup="onKeyUp"
  >
    <fpui-input-label :helper="helper" :helper-title="helperTitle" :helper-position="helperPosition" :required="required" v-if="label || $slots.label">
      <span v-if="!$slots.label">{{ $t(label) }}</span>
      <slot v-if="$slots.label" name="label"></slot>
    </fpui-input-label>
    <div class="fpui-input-text-content">
      <div class="fpui-input-text-error" v-if="!value && requiredError">{{ $t(requiredError) }}</div>
      <div class="fpui-input-text-error" v-show="error" v-tooltip="errorTooltip">
        <i class="icon fp4 fp4-circle-info" v-show="errorTooltip"></i>
        <div class="text">{{ $t(errorMessage) }}</div>
      </div>
      <div class="fpui-input-text-with-prefix-suffix" v-if="prefix || suffix">
        <input
          ref="input"
          :min="min"
          :max="max"
          autocomplete="off"
          oninput="validity.valid||(value='')"
          :name="name"
          :placeholder="$t(placeholder)"
          :type="type"
          :pattern="pattern"
          :step="step"
          :id="id"
          v-model="model"
          :class="className"
          :disabled="disabled"
          @focus="onFocus"
          @focusout="onFocusOut"
          @keyup="$emit('keyup')"
          @click="$emit('click', $event)"
          @blur="$emit('blur', $event)"
        >
        <div class="fpui-input-text-content-prefix" v-if="prefix">{{ prefix }}</div>
        <div class="fpui-input-text-content-suffix" v-if="suffix">{{ suffix }}</div>
      </div>
      <input
        v-else
        ref="input"
        :min="min"
        :max="max"
        :autocomplete="autocompleteAttribute"
        oninput="validity.valid||(value='')"
        :name="name"
        :placeholder="$t(placeholder)"
        :type="type"
        :pattern="pattern"
        :step="step"
        :id="id"
        v-model="model"
        :class="className"
        :disabled="disabled"
        @focus="onFocus"
        @focusout="onFocusOut"
        @keyup="$emit('keyup')"
        @click="$emit('click', $event)"
        @blur="$emit('blur', $event)"
      >
      <span class="optional" v-if="optional">
        <i>{{ $t("fpui-input.optional") }}</i>
      </span>
      <span class="copy" v-if="copy" @click="copyAction">
        <i class="fp4 fp4-copy"></i>
      </span>
      <ul
        class="autocomplete"
        v-if="autocomplete && inAutocomplete.length > 0"
        :class="{ 'autocomplete-top': autocompletePosition === 'top', 'hide-autocomplete': hideAutocomplete }"
        ref="auto-complete-items"
      >
        <li
          v-for="(auto, index) in inAutocomplete"
          :key="`auto-complete-${index}`"
          :disabled="auto.disabled"
          :class="{ selected: auto.active, disabled: auto.disabled, 'category-header': auto.isCategory }"
          @click="auto.isCategory ? null : onAutocomplete(auto)"
          :ref="`auto-complete-${index}`"
        >
          <div class="category-title" v-if="auto.isCategory">{{ auto.category }}</div>
          <div class="autocomplete-content" v-else>
            <div class="autocomplete-value">{{ auto.display_name || auto.label || auto.value }}</div>
            <div class="autocomplete-description" v-if="auto.description">
              <span>{{ auto.description }}</span>
              <i
                class="fp4"
                v-if="auto.descriptionIcon"
                :class="auto.descriptionIcon"
              ></i>
            </div>
          </div>
        </li>
      </ul>
      <i
        class="clear-input fpui fpui-remove"
        @click="model = ''"
        v-if="clearable && autocomplete && value && value.length > 0 && !disabled"
      ></i>
    </div>
  </div>
</template>

<script>
import _isString from 'lodash/isString'

export default {
  props: {
    id: {
      type: String,
      required: false,
      default () {
        return Math.random()
          .toString(36)
          .replace(/[^a-z]+/g, '')
          .substr(2, 10)
      }
    },
    name: { type: String, default: '' },
    value: { type: [String, Number, Array], default: '' },
    color: { type: String, default: 'blue' },
    disabled: { type: Boolean, default: false },
    error: { type: Boolean, default: false },
    errorMessage: { type: String, default: '' },
    errorTooltip: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    autocomplete: { type: Array, default: () => [] },
    autocompletePosition: { type: String, default: '' },
    onAutocompleteSelection: { type: Function, default: null },
    autocompleteCloseOnEsc: { type: Boolean, default: true },
    pattern: { type: RegExp, default: null },
    type: { type: String, default: 'text' },
    step: { type: String, default: 'text' },
    autofocus: { type: Boolean, default: false },
    selectAllOnFocus: { type: Boolean, default: false },
    label: { type: String, default: '' },
    requiredError: { type: String, default: '' },
    required: { type: Boolean, default: false },
    helper: { type: String, default: '' },
    helperTitle: { type: String, default: '' },
    helperPosition: { type: String, default: 'right-end' },
    copy: { type: Boolean },
    clearable: { type: Boolean, default: true },
    theme: { type: String, default: 'v4' },
    optional: { type: Boolean, default: false },
    min: { type: Number, default: null },
    max: { type: Number, default: null },
    prefix: { type: String, default: null },
    suffix: { type: String, default: null }
  },
  data () {
    return {
      inAutocomplete: [],
      focused: false,
      hideAutocomplete: true
    }
  },
  computed: {
    model: {
      get () {
        return this.value
      },
      set (val) {
        if (this.autocomplete) {
          this.updateAutocomplete(val)
        }
        this.$emit('input', val)
      }
    },
    className () {
      const className = [`fpui-input-color-${this.color}`]
      if (this.disabled) className.push('disabled')
      if (this.error) className.push('error')
      if (this.requiredError && !this.value) className.push('error')
      if (this.copy) className.push('with-copy')
      if (this.prefix) className.push('with-prefix')
      if (this.suffix) className.push('with-suffix')
      return className.join(' ')
    },
    themeClass () {
      const className = [this.theme]
      if (this.focused) className.push('focused')
      if (this.error) className.push('error')
      return className.join(' ')
    },
    autocompleteAttribute () {
      // if the autocomplete property is a string, it means it is used
      // as the default html attribute and should be binded to the input tag.
      // Otherwise, use the "off" value instead
      return _isString(this.autocomplete) ? this.autocomplete : 'off'
    }
  },
  watch: {
    autocomplete () {
      this.updateAutocomplete(this.model)
    },
    hideAutocomplete () {
      this.$emit('autocomplete-visible', !this.hideAutocomplete)
    }
  },
  mounted () {
    this.onKeyUp = this.onKeyUp.bind(this)
    if (this.autofocus) this.focus()
    if (!this.value) this.$emit('input', '')
  },
  methods: {
    async copyAction () {
      await navigator.clipboard.writeText(this.value)
      this.$fpuiMessageBlock.success('input.text.copy.success')
    },
    onFocusOut () {
      this.focused = false
      this.$emit('focusout')
    },
    onFocus (e) {
      this.focused = true
      if (this.selectAllOnFocus) e.target.select()
      this.updateAutocomplete(this.model)
    },
    onAutocomplete (val) {
      if (!val?.disabled) {
        if (this.onAutocompleteSelection) {
          return this.onAutocompleteSelection(val)
        }
        this.model = val.value
        this.hideAutocomplete = true
      }
    },
    scrollIntoView (index) {
      this.$nextTick(() => {
        let idx = index
        // Ensure category header is visible when selected
        // It can be hidden when coming from the latest item to the first one
        if (this.inAutocomplete[index]?.category && index === 1) idx = 0
        // if it's the latest item, scroll all the way to the bottom
        if (this.inAutocomplete[index] === this.inAutocomplete[this.inAutocomplete.length - 1]) {
          const containerRef = this.$refs['auto-complete-items']
          containerRef.scrollTo({ top: containerRef.scrollHeight, behavior: 'smooth' })
        } else {
          const ref = this.$refs[`auto-complete-${idx}`]
          const el = Array.isArray(ref) ? ref[0] : ref
          el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        }
      })
    },
    onKeyUp (e) {
      if (this.inAutocomplete.length > 0) {
        let index = 0
        for (let i = 0; i < this.inAutocomplete.length; i++) {
          const auto = this.inAutocomplete[i]
          if (auto.active && !auto.isCategory) index = i
        }
        // Enter key
        if (e.keyCode === 13) {
          const selectedItem = this.inAutocomplete[index]
          if (selectedItem && !selectedItem.isCategory) {
            this.onAutocomplete(selectedItem)
          }
          return
        }
        // Down key
        if (e.keyCode === 40) {
          this.inAutocomplete[index].active = false
          let nextIndex = index + 1
          // Skip category headers
          while (
            nextIndex < this.inAutocomplete.length &&
            (this.inAutocomplete[nextIndex].isCategory ||
              this.inAutocomplete[nextIndex].disabled)
          ) {
            nextIndex++
          }
          if (nextIndex >= this.inAutocomplete.length) {
            // Find first non-category, non-disabled item
            nextIndex = this.inAutocomplete.findIndex(
              (item) => !item.isCategory && !item.disabled
            )
          }
          if (nextIndex >= 0) {
            this.inAutocomplete[nextIndex].active = true
            this.scrollIntoView(nextIndex)
          }
        }
        // Up key
        if (e.keyCode === 38) {
          this.inAutocomplete[index].active = false
          let prevIndex = index - 1
          // Skip category headers
          while (
            prevIndex >= 0 &&
            (this.inAutocomplete[prevIndex].isCategory ||
              this.inAutocomplete[prevIndex].disabled)
          ) {
            prevIndex--
          }
          if (prevIndex < 0) {
            // Find last non-category, non-disabled item
            for (let i = this.inAutocomplete.length - 1; i >= 0; i--) {
              if (
                !this.inAutocomplete[i].isCategory &&
                !this.inAutocomplete[i].disabled
              ) {
                prevIndex = i
                break
              }
            }
          }
          if (prevIndex >= 0) {
            this.inAutocomplete[prevIndex].active = true
            this.scrollIntoView(prevIndex)
          }
        }
        // Esc key, close autocomplete
        if (e.keyCode === 27 && this.autocompleteCloseOnEsc) {
          this.hideAutocomplete = true
          e.stopImmediatePropagation()
          return
        }
      }
      if (e.keyCode === 13) this.$emit('enter', e)
      this.$emit('keyup', e)
    },
    focus () {
      this.$refs.input && this.$refs.input.focus()
    },
    updateAutocomplete (userInput) {
      // autocomplete can either be an array, or used as the default autocomplete html attribute
      // here we only want to handle the first case
      if (
        userInput &&
        userInput.length > 0 &&
        Array.isArray(this.autocomplete)
      ) {
        const filtered = this.autocomplete.filter((auto) => {
          try {
            return (
              new RegExp(userInput, 'i').test(auto?.value) ||
              new RegExp(userInput, 'i').test(auto?.label)
            )
          } catch (err) {
            return false
          }
        })

        // Group by category if categories exist
        const hasCategories = filtered.some((item) => item.category)

        if (hasCategories) {
          // Group items by category
          const grouped = filtered.reduce((acc, item) => {
            const category = item.category || 'Other'
            if (!acc[category]) acc[category] = []
            acc[category].push(item)
            return acc
          }, {})

          // Flatten into display format with category headers
          this.inAutocomplete = []
          let itemIndex = 0
          Object.keys(grouped).forEach((category) => {
            // Add category header
            this.inAutocomplete.push({
              isCategory: true,
              category: category,
              disabled: true
            })

            // Add category items
            grouped[category].forEach((auto) => {
              this.inAutocomplete.push({
                ...auto,
                value: auto.value,
                label: auto.label,
                active: itemIndex === 0 && !auto.disabled,
                disabled: auto.disabled,
                description: auto.description,
                descriptionIcon: auto.descriptionIcon,
                category: auto.category
              })
              if (!auto.disabled) itemIndex++
            })
          })
        } else {
          // No categories, use existing logic
          this.inAutocomplete = filtered.map((auto, index) => {
            return {
              ...auto,
              value: auto.value,
              label: auto.label,
              active: index === 0 && !auto.disabled,
              disabled: auto.disabled,
              description: auto.description,
              descriptionIcon: auto.descriptionIcon
            }
          })
        }

        if (this.inAutocomplete.length > 0) this.hideAutocomplete = false
        else this.hideAutocomplete = true
      } else {
        this.hideAutocomplete = true
      }
    }
  }
}
</script>

<style lang='less'>
@import "~@/shared/styles/_variables.less";

.fpui-input-text.v4 {
  position: relative;
  .fpui-input-text-content input::placeholder {
    color: rgba(62, 69, 80, 0.35);
  }
  .fpui-input-text-content:not(.with-padding) {
    padding: 0;
  }
  .fpui-input-text-error {
    color: #f62172;
    position: absolute;
    line-height: 15px;
    font-size: 12px;
    right: 5px;
    top: 5px;
    display: flex;
    align-items: center;
    cursor: pointer;
    .icon {
      margin-right: 2px;
    }
  }
  .copy {
    width: 40px;
    border-radius: 0 3px 3px 0;
    background-color: rgba(151, 167, 183, 0.12);
    text-align: center;
    height: 34px;
    line-height: 34px;
    vertical-align: middle;
    display: inline-block;
    border: 1px solid @grey_dark;
    cursor: pointer;
    font-size: 16px;
    &:hover {
      background-color: rgba(151, 167, 183, 0.18);
    }
    &:active {
      background-color: rgba(151, 167, 183, 0.24);
    }
  }
  input {
    vertical-align: middle;
    width: 100%;
    border-radius: 3px;
    &.with-copy {
      width: calc(~"100% - 40px");
      border-radius: 3px 0 0 3px;
    }
    height: 34px;
    background-color: rgba(151, 167, 183, 0.06);
    padding: 8px;
    padding-left: 11px;
    border: 1px solid @grey_dark;
    font-size: 14px;
    font-weight: 300;
    outline: none;
    font-weight: 400;
    color: rgb(62, 69, 80);
    font-family: @global-font-family;
    font-weight: 400;
    letter-spacing: normal;
    &:disabled {
      border: unset;
      background-color: rgba(151, 167, 183, 0.06);
      cursor: not-allowed;
      color: rgba(62, 69, 80, 0.65);
    }
    .input-transition();
    &.fpui-input-color-blue:not(.error):focus {
      border-color: @blue;
    }
    &.fpui-input-color-green:not(.error):focus {
      border-color: @green;
    }
    &.fpui-input-color-red:focus,
    &.error {
      border-color: @red;
    }
    &.fpui-input-color-yellow:not(.error):focus {
      border-color: @yellow;
    }
  }
  .fpui-input-text-with-prefix-suffix {
    display: flex;
    align-items: center;
    justify-content: space-between;
    .fpui-input-text-content-prefix {
      order: 1;
      padding-left: 10px;
      border-top-left-radius: 3px;
      border-bottom-left-radius: 3px;
      border-top: 1px solid @grey_dark;
      border-left: 1px solid @grey_dark;
      border-bottom: 1px solid @grey_dark;
      height: 34px;
      line-height: 34px;
      background-color: rgba(151, 167, 183, 0.06);
      .input-transition();
    }
    .fpui-input-text-content-suffix {
      order: 3;
      padding-right: 10px;
      border-top-right-radius: 3px;
      border-bottom-right-radius: 3px;
      border-top: 1px solid @grey_dark;
      border-right: 1px solid @grey_dark;
      border-bottom: 1px solid @grey_dark;
      height: 34px;
      line-height: 34px;
      background-color: rgba(151, 167, 183, 0.06);
      .input-transition();
    }
    input {
      order: 2;
      flex: 0 1 100%;
      border-radius: 0;
      border-left: none;
      border-right: none;
      padding-left: 5px;
      &:focus {
        & ~ div[class^="fpui-input-text-content-"] {
          border-color: @blue;
        }
      }
    }
  }
  .optional {
    position: absolute;
    right: 15px;
    top: 50%;
    color: rgba(62, 69, 80, 0.25);
    font-size: 13px;
  }
  .autocomplete {
    padding: 0;
    margin: 0;
    list-style: none;
    position: absolute;
    left: 0;
    width: 100%;
    z-index: 1000;
    opacity: 1;
    background-color: white;
    display: block;
    border-radius: 7px;
    box-shadow: 0px 3px 13px 0px rgba(151, 167, 183, 0.3);
    color: #3e4550;
    font-size: 14px;
    font-weight: 400;
    li {
      padding: 12px 20px;
      cursor: pointer;
      &:hover,
      &.selected {
        background-color: #f6f9fc;
      }
      &.disabled {
        cursor: not-allowed;
        .autocomplete-value {
          opacity: 0.5;
        }
      }
      &.category-header {
        cursor: default;
        padding: 8px 20px;
        user-select: none;
        .category-title {
          font-weight: 600;
          font-size: 12px;
          text-transform: uppercase;
          color: #6c757d;
          letter-spacing: 0.5px;
        }
      }
      .autocomplete-description {
        color: #97a7b7;
        font-size: 12px;
        font-style: italic;
        display: flex;
        align-items: center;
        gap: 5px;
      }
    }
    &.autocomplete-top {
      bottom: 40px;
      overflow-y: scroll;
    }
    &.hide-autocomplete {
      display: none;
    }
  }
  .clear-input {
    position: absolute;
    right: 5px;
    top: 0px;
    color: @blue;
    cursor: pointer;
    font-size: 15px;
  }
}
</style>
